import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import { CrewList, TableColumn, CrewChange } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import QuickCrewList from '../../components/HR/QuickCrewList';
import { exportToExcel } from '../../utils/exportToExcel';
import { resolveCrewStint } from '../../utils/crewStint';
import { PageHeader, StatsCards } from '../../components/Common';
import type { StatCard } from '../../components/Common';
import ActiveCrewInfoBanner from './ActiveCrewInfoBanner';
import ActiveCrewFilters from './ActiveCrewFilters';
import { generatePrintHtml } from './ActiveCrewPrintTemplate';
import { arabicEmployeeName } from '../../utils/employeeName';

const ActiveCrewListArabic: React.FC = () => {
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const { data: branches = [] } = useBranches({ populate: '*' });
  const [showQuickList, setShowQuickList] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');

  const { data: crewChangesData = [] } = useCrewChanges({ pagination: { pageSize: 1000 } });
  const crewChanges = crewChangesData as CrewChange[];

  const [activeCrewList, setActiveCrewList] = useState<CrewList[]>([]);

  const findEmployee = useCallback((employeeId: string) => {
    return employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
  }, [employees]);

  const getEmployeeName = useCallback((employeeId: string) => arabicEmployeeName(findEmployee(employeeId)) || '-', [findEmployee]);
  const getEmployeeRank = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp ? (emp.rank || emp.position || '-') : '-';
  }, [findEmployee]);
  const getEmployeeNationality = useCallback((employeeId: string) => findEmployee(employeeId)?.nationality || '-', [findEmployee]);
  const getEmployeeBirthDate = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.birthDate ? new Date(emp.birthDate).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);
  const getEmployeeSeamanBookNumber = useCallback((employeeId: string) => findEmployee(employeeId)?.seamanBookNumber || '-', [findEmployee]);
  const getEmployeeSeamanBookExpiry = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.seamanBookExpiry ? new Date(emp.seamanBookExpiry).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id?.toString() === branchId || b.documentId === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const generateCrewList = useCallback(() => {
    const byEmployee: { [key: string]: CrewChange[] } = {};
    crewChanges.forEach(change => {
      (byEmployee[change.employeeId!] ||= []).push(change);
    });
    // Active = the employee's latest crew change is Sign On or Continue.
    // joinDate = the sign_on that began the current uninterrupted stay — a
    // `continue` must not reset it. lastUpdate tracks the latest change date.
    const crewList: CrewList[] = Object.entries(byEmployee).flatMap(([employeeId, changes]) => {
      const stint = resolveCrewStint(changes);
      if (!stint.aboard || !stint.latest) return [];
      return [{
        id: `crew_${employeeId}`,
        employeeId,
        joinDate: stint.joinDate!,
        lastUpdate: stint.lastUpdate || undefined,
        branchId: stint.latest.branchId!,
        status: 'active' as const,
      }];
    });
    setActiveCrewList(crewList);
  }, [crewChanges]);

  useEffect(() => { generateCrewList(); }, [generateCrewList]);

  const uniqueBranches = Array.from(new Set(activeCrewList.map(crew => crew.branchId).filter(Boolean)));
  const uniqueRanks = Array.from(new Set(activeCrewList.map(crew => getEmployeeRank(crew.employeeId)).filter(Boolean)));

  const filteredActiveCrewList = useMemo(() => {
    return activeCrewList.filter(crew => {
      const matchesSearch = !searchTerm || (
        getEmployeeName(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getBranchName(crew.branchId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEmployeeRank(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || crew.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || crew.branchId === branchFilter;
      const matchesRank = rankFilter === 'all' || getEmployeeRank(crew.employeeId) === rankFilter;
      return matchesSearch && matchesStatus && matchesBranch && matchesRank;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrewList, searchTerm, statusFilter, branchFilter, rankFilter]);

  const handleExport = () => {
    const exportData = filteredActiveCrewList.map(crew => ({
      'اسم الموظف': getEmployeeName(crew.employeeId),
      'الرتبة': getEmployeeRank(crew.employeeId),
      'السفينة/المقر': getBranchName(crew.branchId),
      'الجنسية': getEmployeeNationality(crew.employeeId),
      'تاريخ الميلاد': getEmployeeBirthDate(crew.employeeId),
      'رقم الجواز البحري': getEmployeeSeamanBookNumber(crew.employeeId),
      'انتهاء الجواز البحري': getEmployeeSeamanBookExpiry(crew.employeeId),
      'تاريخ الانضمام': new Date(crew.joinDate).toLocaleDateString('ar-EG'),
      'تاريخ آخر تحديث': crew.lastUpdate ? new Date(crew.lastUpdate).toLocaleDateString('ar-EG') : '-',
      'الحالة': crew.status === 'active' ? 'نشط' : crew.status
    }));
    exportToExcel(exportData, `active_crew_list_arabic_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const crewData = filteredActiveCrewList.map(crew => ({
      name: getEmployeeName(crew.employeeId),
      rank: getEmployeeRank(crew.employeeId),
      nationality: getEmployeeNationality(crew.employeeId),
      dateOfBirth: getEmployeeBirthDate(crew.employeeId),
      seamanBookNo: getEmployeeSeamanBookNumber(crew.employeeId),
      seamanBookExpiry: getEmployeeSeamanBookExpiry(crew.employeeId),
      dateOfJoin: new Date(crew.joinDate).toLocaleDateString('ar-EG')
    }));
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generatePrintHtml(crewData));
      printWindow.document.close();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setRankFilter('all');
  };

  const columns: TableColumn[] = [
    { key: 'employeeId', label: 'اسم الموظف', sortable: true, render: (_: string, item: CrewList) => getEmployeeName(item.employeeId!) },
    { key: 'employeeId', label: 'الرتبة', sortable: true, render: (_: string, item: CrewList) => getEmployeeRank(item.employeeId!) },
    { key: 'branchId', label: 'السفينة/المقر', sortable: true, render: (_: string, item: CrewList) => getBranchName(item.branchId!) },
    { key: 'employeeId', label: 'الجنسية', sortable: true, render: (_: string, item: CrewList) => getEmployeeNationality(item.employeeId!) },
    { key: 'employeeId', label: 'تاريخ الميلاد', sortable: true, render: (_: string, item: CrewList) => getEmployeeBirthDate(item.employeeId!) },
    { key: 'employeeId', label: 'رقم الجواز البحري', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookNumber(item.employeeId!) },
    { key: 'employeeId', label: 'انتهاء الجواز البحري', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookExpiry(item.employeeId!) },
    { key: 'joinDate', label: 'تاريخ الانضمام', sortable: true, render: (value: string) => new Date(value).toLocaleDateString('ar-EG') },
    { key: 'lastUpdate', label: 'تاريخ آخر تحديث', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('ar-EG') : '-' },
    { key: 'status', label: 'الحالة', sortable: true, render: (value: string) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {value === 'active' ? 'نشط' : value}
      </span>
    )}
  ];

  const stats: StatCard[] = [
    { label: 'إجمالي الطاقم النشط', value: activeCrewList.length, color: 'green' },
    { label: 'الفروع النشطة', value: new Set(activeCrewList.map(crew => crew.branchId)).size, color: 'blue' },
    { label: 'الرتب الفريدة', value: uniqueRanks.length, color: 'yellow' },
    { label: 'آخر تحديث', value: new Date().toLocaleDateString('ar-EG'), color: 'purple' },
  ];

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader
        title="قائمة الطاقم النشط"
        description="أعضاء الطاقم الحاليون الذين تم تسجيلهم أو يواصلون مهامهم"
        actions={[{ label: 'تحديث القائمة', onClick: generateCrewList, variant: 'primary' }]}
      />

      <StatsCards stats={stats} />

      <ActiveCrewInfoBanner onQuickPrint={() => setShowQuickList(true)} />

      <ActiveCrewFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        branchFilter={branchFilter}
        onBranchFilterChange={setBranchFilter}
        rankFilter={rankFilter}
        onRankFilterChange={setRankFilter}
        uniqueBranches={uniqueBranches}
        uniqueRanks={uniqueRanks}
        getBranchName={getBranchName}
        filteredCount={filteredActiveCrewList.length}
        totalCount={activeCrewList.length}
        onExport={handleExport}
        onPrint={handlePrint}
        onClearFilters={handleClearFilters}
      />

      <Table
        columns={columns}
        data={filteredActiveCrewList}
        showAddButton={false}
        showEditButton={false}
        showDeleteButton={false}
        searchable={false}
      />

      {showQuickList && (
        <QuickCrewList onClose={() => setShowQuickList(false)} language="ar" />
      )}
    </div>
  );
};

export default ActiveCrewListArabic;
